import React, { useState } from "react";
import "./PolarianALMSolutionPage.css";
import BannerImage from "../../../assets/other_image/polarian.jpg";
import PolarianALMSolutionOverview from "./PolarianALMSolutionOverview";
import Letsgetintouch from "../../PLMImplementation/BusinessProcessAndChangeManagement/Letsgetintouch";
import Banner from "../SolutionBanner/Banner";
import FloatingContactUs from "../../floting_contact/FloatingContactUs";
import BookDemoSection from "./BookDemoSection";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/* FAQ Data */
const faqData = [
  { question: "What is Polarion ALM and what is it used for?", answer: "Polarion ALM is a cloud-based platform by Siemens for managing the complete software development lifecycle, including requirements, testing, changes, and traceability." },
  { question: "Is Polarion ALM suitable for both small and large businesses?", answer: "Yes. It's scalable for startups, SMBs, and large enterprises with on-premise and cloud deployment options." },
  { question: "What industries commonly use Polarion ALM?", answer: "Automotive, aerospace, medical devices, industrial, electronics, and other regulated sectors." },
  { question: "What is involved in a typical Polarion ALM implementation?", answer: "Design, setup of modules, custom workflows, tool integrations, user management, training, and onboarding." },
  { question: "Can Polarion ALM integrate with Jira, Git, or Teamcenter?", answer: "Yes. Nextech provides seamless integrations with Jira, Git, Jenkins, Azure DevOps, and Teamcenter." },
  { question: "How long does a Polarion ALM deployment take?", answer: "Most projects take 2–12 weeks depending on size and complexity." },
  { question: "How does Polarion ALM support regulatory compliance?", answer: "It enables ISO, GxP, FDA, and DO-178C compliance via version control, approvals, audit logs, and traceability matrices." },
  { question: "Can Nextech migrate our data from HP ALM or DOORS to Polarion?", answer: "Yes. We offer validated migrations with mapping, testing, and user validation." },
  { question: "Can I create custom reports and dashboards in Polarion ALM?", answer: "Yes, and Nextech can help tailor reports and dashboards based on KPIs and compliance needs." }
];

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button className="faq-question" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span>{question}</span>
        <span>{open ? "−" : "+"}</span>
      </button>
      {open && <div className="faq-answer">{answer}</div>}
    </div>
  );
};

const SectionHeader = ({ title, subtitle }) => (
  <header className="section-header">
    <h2 className="section-title">{title}</h2>
    {subtitle && <p className="section-subtitle">{subtitle}</p>}
  </header>
);

const TileGrid = ({ items }) => (
  <div className="tile-grid-3x2">
    {items.map((item, index) => (
      <article className="feature-tile" key={index}>
        {item.icon && (
          <div className="tile-icon">
            {typeof item.icon === "string" && item.icon.startsWith("http") ? (
              <img src={item.icon} alt={item.title} width="48" height="48" />
            ) : (
              <span className="emoji-icon">{item.icon}</span>
            )}
          </div>
        )}
        <h3 className="tile-title">{item.title}</h3>
        <p className="tile-content">{item.content}</p>
      </article>
    ))}
  </div>
);

const PLMSolutionALMPage = () => {
  /* --- Polarion Featured Solutions --- */
  const featuredSolutionsItems = [
    {
      title: "Requirements Management",
      content:
        "Capture, manage, and trace requirements in real time — ensuring accuracy, transparency, and full traceability from concept to release."
    },
    {
      title: "Change & Configuration Management",
      content:
        "Control evolving requirements and system configurations with robust change workflows that reduce errors and improve collaboration."
    },
    {
      title: "Risk & Impact Management",
      content:
        "Identify, assess, and mitigate risks early with integrated risk tracking tied to requirements, tests, and compliance artifacts."
    },
    {
      title: "Test & Quality Management",
      content:
        "Seamlessly link test cases to requirements and automate validation processes — ensuring quality is built in at every stage."
    },
    {
      title: "Project Planning & Tracking",
      content:
        "Plan and monitor project progress with real-time dashboards, milestone tracking, and Agile/Hybrid methodology support."
    },
    {
      title: "Configuration & Workflow Customization",
      content:
        "Adapt Polarion to your business processes with flexible templates, configurable workflows, and tailored permission sets."
    }
  ];

  /* --- Our Solutions --- */
  const ourSolutionsItems = [
    {
      icon: "🔧",
      title: "Complete Polarion ALM Implementation",
      content: "Fast, efficient deployment customized to your organization."
    },
    {
      icon: "🔗",
      title: "Seamless Integration with Teamcenter & PLM Systems",
      content: "Unify your engineering and software lifecycle."
    },
    {
      icon: "🛠️",
      title: "Custom Configuration & Workflow Optimization",
      content: "Align Polarion to your processes, not the other way around."
    },
    {
      icon: "📘",
      title: "Role-Based Training & Knowledge Transfer",
      content: "Empower your teams with practical, hands-on learning."
    },
    {
      icon: "🔍",
      title: "Expert Consulting & Process Mapping",
      content: "Get strategic guidance to maximize your ALM investment."
    },
    {
      icon: "🔄",
      title: "Managed Support & Continuous Enhancements",
      content: "Stay ahead with ongoing maintenance and upgrades."
    }
  ];

  /* --- Why Choose NextechPLM --- */
  const whyNextechItems = [
    {
      icon: "🧠",
      title: "Specialized ALM–PLM Integration Expertise",
      content:
        "We bridge the gap between software and hardware by connecting Polarion with Teamcenter and other PLM platforms — enabling unified product development."
    },
    {
      icon: "🏭",
      title: "Industry-Tailored Implementation Frameworks",
      content:
        "Unlike generic providers, we deliver Polarion setups customized to your domain — from automotive and aerospace to medical and electronics."
    },
    {
      icon: "🔁",
      title: "Complete Lifecycle Services – From Start to Scale",
      content:
        "From planning and installation to training, upgrades, and ongoing optimization — we manage your ALM journey at every stage."
    },
    {
      icon: "✅",
      title: "Built-In Compliance & Validation Workflows",
      content:
        "We provide pre-validated templates and documentation aligned with FDA, ISO, GxP, and other regulatory frameworks out of the box."
    },
    {
      icon: "📊",
      title: "Business-Driven Dashboards & Reporting",
      content:
        "Our custom dashboards give you actionable insights into requirements, testing, risks, and compliance — all aligned with KPIs that matter."
    },
    {
      icon: "🧰",
      title: "Comprehensive Enablement & Support",
      content:
        "We don’t walk away after go-live. We offer hands-on training, performance tuning, and expert support to keep your system performing at its best."
    }
  ];

  return (
    <>
      {/* ✅ Meta Tags for SEO */}
    <Helmet>
  <title>Polarion ALM Solutions Services in Pune | NexTech PLM</title>
  <meta
    name="description"
    content="Manage your application lifecycle efficiently with Polarion ALM solutions in Pune, India. Nextech PLM delivers robust and integrated services."
  />
  <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
</Helmet>

    <main className="polarion-solution-page">
      <Banner image={BannerImage} heading="Polarion ALM Solutions" />
      <FloatingContactUs />
      <PolarianALMSolutionOverview />
{/* === Benefits Section === */}
<section className="section benefits-section">
  <div className="container">
    <div className="row g-4 align-items-center">

      {/* Benefits Section */}
      <div className="col-md-6 col-12">
        <div className="benefits-card">
          <h3 className="benefits-heading">
            Business Benefits of Polarion with NexTech
          </h3>
          <ul className="benefits-list">
            <li>
              <span className="benefit-icon">✔</span> Accelerate product development with unified workflows that eliminate silos.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Ensure end-to-end traceability from requirements to release.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Maximize ROI with tailored Polarion implementations.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Enhance team productivity with real-time visibility and collaboration.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Reduce risk and rework via built-in quality and test management.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Adapt fast to change with agile-ready frameworks and toolchain integration.
            </li>
            <li>
              <span className="benefit-icon">✔</span> Future-proof lifecycle management with scalable cloud-based solutions.
            </li>
          </ul>
        </div>
      </div>

      {/* Quick Links Section */}
      <div className="col-md-6 col-12 d-flex align-items-center">
        <div className="quick-links">
          <h4>Quick Links</h4>
          <ul>
            <li>
              <NavLink to={"/teamcenter/plm-document-management"}>
                Document Management
              </NavLink>
            </li>
            <li>
              <NavLink to={"/teamcenter/manufacturing-data-process-management"}>
                Manufacturing Data and Process Management
              </NavLink>
            </li>
            <li>
              <NavLink to={"/teamcenter/product-bom-variability-management"}>
                Product BOM and Variability Management
              </NavLink>
            </li>
            <li>
              <NavLink to={"/teamcenter/community-collaboration"}>
                Teamcenter Community Collaboration
              </NavLink>
            </li>
            <li>
              <NavLink to={"/teamcenter/cloud-implementation"}>
                Teamcenter Implementation on Cloud
              </NavLink>
            </li>
            <li>
              <NavLink to={"/teamcenter/ecad-integration"}>
                ECAD Integration
              </NavLink>
            </li>
            <li>
              <NavLink to={"/teamcenter/mcad-integration"}>
                MCAD Integration
              </NavLink>
            </li>
          </ul>
        </div>
      </div>

    </div>
  </div>
</section>


      <BookDemoSection />

      {/* === Polarion Featured Solutions === */}
      <section className="section featured-solutions-section">
        <div className="container">
          <SectionHeader
            title="Polarion Featured Solutions"
            subtitle="Explore the Core Capabilities that Power Innovation, Compliance, and Speed."
          />
          <TileGrid items={featuredSolutionsItems} />
          <p className="section-outro">
            With <strong>NextechPLM’s expertise</strong>, these powerful Polarion features aren’t just deployed —
            they’re optimized to enhance your team’s productivity, ensure compliance, and accelerate your product success.
          </p>
        </div>
      </section>

      {/* === Video Section === */}
      <section className="video-promo">
        <div className="video-promo-container">
          <div className="video-promo-content">
            <h2 className="video-promo-heading">
              Transform Your Development Process with Polarion ALM
            </h2>
            <p className="video-promo-text">
              Experience how our solution drives innovation through seamless real-time collaboration
              and end-to-end lifecycle visibility across your entire organization.
            </p>
            <ul className="video-promo-features">
              <li className="feature-item"><span className="feature-icon">🚀</span>Accelerate time-to-market with streamlined workflows</li>
              <li className="feature-item"><span className="feature-icon">🔗</span>Maintain complete traceability from requirements to release</li>
              <li className="feature-item"><span className="feature-icon">🛡️</span>Ensure compliance with built-in quality gates</li>
            </ul>
          </div>
          <div className="video-promo-player">
            <div className="video-wrapper">
              <iframe
                src="https://www.youtube.com/embed/QTKR2LbLbD0?autoplay=0&rel=0&modestbranding=1"
                title="Polarion ALM Solution Overview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
            <p className="video-caption">Watch our 3-minute demo to see Polarion ALM in action</p>
          </div>
        </div>
      </section>

      {/* === Our Solutions === */}
      <section className="section our-solutions-section">
        <div className="container">
          <SectionHeader title="Our Solutions" />
          <p className="section-intro">
            At <strong>NextechPLM</strong>, we deliver end-to-end Polarion ALM solutions that go beyond implementation —
            seamlessly integrating with Teamcenter, PLM systems, and your existing workflows. Our experts bring deep domain
            knowledge, proven methodologies, and scalable configurations tailored to your business needs.
          </p>
          <TileGrid items={ourSolutionsItems} />
          <p className="section-outro">
            With our proven expertise and industry experience, NextechPLM transforms your product development landscape —
            driving innovation, ensuring compliance, and boosting business performance with confidence.<br />
            <strong>Partner with NextechPLM — Turn Polarion ALM into a Catalyst for Digital Excellence.</strong>
          </p>
        </div>
      </section>

      {/* === Why Choose NextechPLM === */}
      <section className="section why-nextech-section">
        <div className="container">
          <SectionHeader title="Why Choose NextechPLM for Polarion ALM Implementation?" />
          <p className="section-intro">
            More than just implementation — we deliver fully integrated, future-ready ALM solutions tailored to your business.
            At NextechPLM, we go beyond standard deployment. Our approach combines deep technical expertise with industry-specific
            knowledge to deliver Polarion ALM solutions that are fully integrated, compliance-ready, and designed to scale with
            your business. Whether you need seamless connectivity to Teamcenter, custom workflows, or post-go-live enablement —
            we deliver a truly end-to-end experience.
          </p>
          <TileGrid items={whyNextechItems} />
          <p className="section-outro">
            With NextechPLM, you get more than a vendor — you gain a strategic partner committed to delivering measurable results
            through smarter, faster, and fully integrated ALM solutions.
          </p>
        </div>
      </section>

      {/* === FAQs === */}
      <section className="section faq-section">
        <div className="container">
          <SectionHeader title="Polarion ALM FAQs" />
          <div className="faq-container">
            {faqData.map((item, i) => (
              <FAQItem key={i} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      <Letsgetintouch />
    </main>
</>
  );
};

export default PLMSolutionALMPage;
